import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Alert, Box, Button, Link as BaseLink, Tooltip } from '@mui/material';
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined';
import {
  MaterialReactTable,
  MRT_ColumnDef,
  useMaterialReactTable,
} from 'material-react-table';
import { Link } from 'react-router-dom';
import { DateTime } from 'luxon';
import {
  DELETE_STANDARD_IMPORT_MUTATION,
  DELETE_SMART_IMPORT_MUTATION,
  MY_SMART_IMPORTS_QUERY,
} from '../../gql';
import { Main } from '../../components';
import {
  formatImportDuration,
  ImportStatusChip,
  isImportActive,
  isImportCancellable,
} from './importStatus';
import { SmartImportSummary } from './types';
import { FormattedMessage, useIntl } from 'react-intl';
import { useMaterialReactTableLocalization } from '../../i18n';

const gridStyles = { marginBottom: 6, display: 'grid' };
const toolbarStyles = { display: 'flex', gap: 1, p: '4px' };

function formatDate(value: string | null): string {
  return value ? DateTime.fromISO(value).toFormat('dd.LL.yyyy HH:mm') : '—';
}

interface ImportRowActionsProps {
  id: string;
  importMode: SmartImportSummary['importMode'];
  status: SmartImportSummary['status'];
  onCancel: (
    id: string,
    importMode: SmartImportSummary['importMode']
  ) => Promise<void>;
}

const ImportRowActions = React.memo(
  ({ id, importMode, status, onCancel }: ImportRowActionsProps) => {
    const [cancelling, setCancelling] = useState(false);
    const handleCancel = useCallback(async () => {
      setCancelling(true);

      try {
        await onCancel(id, importMode);
      } finally {
        setCancelling(false);
      }
    }, [id, importMode, onCancel]);

    if (!isImportCancellable(status)) {
      return null;
    }

    return (
      <Button
        color="error"
        disabled={cancelling}
        onClick={handleCancel}
        size="small"
        startIcon={<StopCircleOutlinedIcon />}
      >
        <FormattedMessage id="imports.stop" defaultMessage="Stop" />
      </Button>
    );
  }
);

export const ImportsList = React.memo(() => {
  const intl = useIntl();
  const localization = useMaterialReactTableLocalization();
  const [now, setNow] = useState(Date.now());
  const [cancelError, setCancelError] = useState<string | null>(null);
  const { data, error, loading, refetch, startPolling, stopPolling } = useQuery(
    MY_SMART_IMPORTS_QUERY,
    {
      variables: { first: 100 },
      fetchPolicy: 'cache-and-network',
    }
  );
  const [cancelImport] = useMutation(DELETE_SMART_IMPORT_MUTATION);
  const [cancelStandardImport] = useMutation(DELETE_STANDARD_IMPORT_MUTATION);
  const rows = useMemo<SmartImportSummary[]>(
    () =>
      (data?.mySmartImports?.edges || []).map(
        (edge: { node: SmartImportSummary }) => edge.node
      ),
    [data?.mySmartImports?.edges]
  );
  const hasActiveImport = rows.some((row) => isImportActive(row.status));
  const createDisabled = loading || hasActiveImport;

  useEffect(() => {
    if (hasActiveImport) {
      startPolling(5000);
    } else {
      stopPolling();
    }

    return stopPolling;
  }, [hasActiveImport, startPolling, stopPolling]);

  useEffect(() => {
    if (!hasActiveImport) {
      return;
    }

    const timer = window.setInterval(() => setNow(Date.now()), 1000);

    return () => window.clearInterval(timer);
  }, [hasActiveImport]);

  const handleCancel = useCallback(
    async (id: string, importMode: SmartImportSummary['importMode']) => {
      setCancelError(null);

      try {
        const cancel =
          importMode === 'standard' ? cancelStandardImport : cancelImport;

        await cancel({ variables: { id } });
        await refetch();
      } catch {
        setCancelError(
          intl.formatMessage({
            id: 'imports.error.stop',
            defaultMessage: 'The import could not be stopped.',
          })
        );
      }
    },
    [cancelImport, cancelStandardImport, intl, refetch]
  );

  const columns = useMemo<MRT_ColumnDef<SmartImportSummary>[]>(
    () => [
      {
        accessorKey: 'id',
        header: intl.formatMessage({ id: 'general.id', defaultMessage: 'ID' }),
        size: 180,
        Cell: ({ cell, row }) => (
          <BaseLink
            component={Link}
            to={
              row.original.importMode === 'standard'
                ? `/imports/standard/${cell.getValue<string>()}/workflow`
                : `/imports/${cell.getValue<string>()}`
            }
            underline="hover"
          >
            {cell.getValue<string>()}
          </BaseLink>
        ),
      },
      {
        accessorKey: 'originalFilename',
        header: intl.formatMessage({
          id: 'imports.file',
          defaultMessage: 'File',
        }),
        size: 180,
      },
      {
        accessorKey: 'status',
        header: intl.formatMessage({
          id: 'imports.state',
          defaultMessage: 'State',
        }),
        size: 160,
        Cell: ({ cell }) => (
          <ImportStatusChip
            status={cell.getValue<SmartImportSummary['status']>()}
          />
        ),
      },
      {
        accessorFn: (row) => DateTime.fromISO(row.createdAt).toJSDate(),
        header: intl.formatMessage({
          id: 'general.created',
          defaultMessage: 'Created',
        }),
        size: 150,
        Cell: ({ cell }) =>
          DateTime.fromJSDate(cell.getValue<Date>()).toFormat(
            'dd.LL.yyyy HH:mm'
          ),
      },
      {
        accessorKey: 'startedAt',
        header: intl.formatMessage({
          id: 'imports.started',
          defaultMessage: 'Started',
        }),
        size: 150,
        Cell: ({ cell }) => formatDate(cell.getValue<string | null>()),
      },
      {
        id: 'duration',
        header: intl.formatMessage({
          id: 'imports.runningTime',
          defaultMessage: 'Running time',
        }),
        size: 120,
        Cell: ({ row }) =>
          formatImportDuration(
            row.original.startedAt,
            row.original.completedAt,
            row.original.updatedAt,
            row.original.status,
            now,
            intl.formatMessage({
              id: 'imports.notStarted',
              defaultMessage: 'Not started',
            })
          ),
      },
      {
        id: 'progress',
        header: intl.formatMessage({
          id: 'imports.progress',
          defaultMessage: 'Progress',
        }),
        size: 130,
        Cell: ({ row }) =>
          `${row.original.progress.processedRowCount} / ${row.original.progress.totalRowCount}`,
      },
      {
        id: 'actions',
        header: '',
        size: 90,
        Cell: ({ row }) => (
          <ImportRowActions
            id={row.original.id}
            importMode={row.original.importMode}
            status={row.original.status}
            onCancel={handleCancel}
          />
        ),
      },
    ],
    [handleCancel, intl, now]
  );

  const renderToolbarActions = useCallback(
    () => (
      <Box sx={toolbarStyles}>
        <Tooltip
          title={
            createDisabled
              ? intl.formatMessage({
                  id: 'imports.create.disabled',
                  defaultMessage:
                    'Finish or cancel the current import before creating another.',
                })
              : ''
          }
        >
          <span>
            <Button
              component={Link}
              disabled={createDisabled}
              to="/imports/create"
              variant="contained"
            >
              <FormattedMessage
                id="imports.create"
                defaultMessage="Create import"
              />
            </Button>
          </span>
        </Tooltip>
      </Box>
    ),
    [createDisabled, intl]
  );

  const table = useMaterialReactTable({
    columns,
    data: rows,
    localization,
    enableColumnActions: false,
    enableColumnFilters: false,
    enableToolbarInternalActions: false,
    initialState: {
      density: 'compact',
      pagination: { pageIndex: 0, pageSize: 50 },
    },
    state: { isLoading: loading },
    muiPaginationProps: { showRowsPerPage: false },
    muiTablePaperProps: { sx: gridStyles },
    renderTopToolbarCustomActions: renderToolbarActions,
  });

  return (
    <Main>
      {error && (
        <Alert severity="error">
          <FormattedMessage
            id="imports.error.load"
            defaultMessage="Could not load imports."
          />
        </Alert>
      )}
      {cancelError && <Alert severity="error">{cancelError}</Alert>}
      <MaterialReactTable table={table} />
    </Main>
  );
});
